import { useState, useId, useEffect } from 'react';
import { authAPI } from 'services/newPlatformAuth.service';
import AppnextLogo from 'assets/images/Appnext_Logo.png';
import { Loader, Buttons } from 'components';
import { isPasswordResetRequired } from './PasswordResetRequired';
import * as S from './UserLogin.styled';

const RESEND_COOLDOWN_SECONDS = 60;
const CODE_LENGTH = 6;

export const Login2fa = ({
  challengeToken,
  maskedEmail,
  onBack,
  onSuccess,
  onPasswordResetRequired,
}) => {
  const formId = useId();
  const btnId = useId();
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [resendSeconds, setResendSeconds] = useState(RESEND_COOLDOWN_SECONDS);

  const [verifyAction, verifyResult] = authAPI.useVerifyLogin2faMutation();
  const [resendAction, resendResult] = authAPI.useResendLogin2faMutation();

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;

    const timer = setInterval(() => {
      setResendSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendSeconds]);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(value);
    if (codeError) setCodeError('');
  };

  const handleVerify = async () => {
    if (code.length !== CODE_LENGTH) {
      setCodeError(`Please enter the ${CODE_LENGTH}-digit code`);
      return;
    }

    try {
      const res = await verifyAction({
        ChallengeToken: challengeToken,
        Code: code,
      }).unwrap();

      if (!res?.access_token) {
        setCodeError('Verification failed');
        return;
      }

      onSuccess(res);
    } catch (err) {
      // the flag may have been set after the code was sent
      if (isPasswordResetRequired(err)) {
        onPasswordResetRequired?.(err);
        return;
      }
      setCodeError(err.data?.Message || 'Invalid verification code');
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0 || resendResult.isLoading) return;

    try {
      await resendAction({ ChallengeToken: challengeToken }).unwrap();
      setCode('');
      setCodeError('');
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const message = err.data?.Message || 'Failed to resend code';
      setCodeError(message);

      const waitMatch = message.match(/wait (\d+) seconds/i);
      if (waitMatch) {
        setResendSeconds(Number(waitMatch[1]));
      }
    }
  };

  const isLoading = verifyResult.isLoading || resendResult.isLoading;

  return (
    <>
      {isLoading && <Loader.Global delay={100} />}
      <S.StyledForm id={formId}>
        <div style={{ marginBottom: '24px' }}>
          <S.FlexCenter style={{ marginBottom: '16px' }}>
            <S.Label>Verify Login</S.Label>
            <img src={AppnextLogo} alt='Appnext Logo' height={33} />
          </S.FlexCenter>
          <S.SeparatorGradient />
        </div>

        <S.Paragraph style={{ marginBottom: '24px', textAlign: 'center' }}>
          Enter the {CODE_LENGTH}-digit code sent to{' '}
          <span style={{ color: '#fff' }}>{maskedEmail}</span>
        </S.Paragraph>

        <S.InputGroup>
          <S.InputLabel htmlFor='code-input'>Verification Code:</S.InputLabel>
          <S.InputWrapper error={Boolean(codeError)}>
            <S.StyledInput
              placeholder='000000'
              name='code'
              id='code-input'
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              maxLength={CODE_LENGTH}
              value={code}
              onChange={handleCodeChange}
              onFocus={() => setCodeError('')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleVerify();
                }
              }}
              style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '20px' }}
            />
          </S.InputWrapper>
          {codeError && <S.ErrorText>{codeError}</S.ErrorText>}
        </S.InputGroup>

        <S.BtnBox>
          <Buttons.PrimaryNormal
            style={{ width: '100%' }}
            id={btnId}
            onClick={handleVerify}
            isDisabled={code.length !== CODE_LENGTH || isLoading}
          >
            Verify
          </Buttons.PrimaryNormal>
        </S.BtnBox>

        <S.Footer style={{ flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          <S.Paragraph style={{ textAlign: 'center' }}>
            Didn&apos;t receive the code?{' '}
            {resendSeconds > 0 ? (
              <span>Resend in {resendSeconds}s</span>
            ) : (
              <S.TextButton type='button' onClick={handleResend} disabled={isLoading}>
                Resend Code
              </S.TextButton>
            )}
          </S.Paragraph>
          <S.TextButton type='button' onClick={onBack}>
            Back to Login
          </S.TextButton>
        </S.Footer>
      </S.StyledForm>
    </>
  );
};
